import { FilterGroup } from "components/Omnisearch/FilterPopover";
import { FilterKey } from "hooks/useOmnisearchDatagrid";

// Filter configuration for the data-pipeline review grids (PipelineRunResults).
//
// The grids filter server-side via omnisearch `key:value` tokens, and the backend
// (wastePipelineRunResultProvider) only resolves the `keyed` shorthands declared on
// WASTE_MANIFEST_FIELD_MAP. So the popover offers those shorthand keys via
// `additionalFilterColumns`, with the data columns excluded from filtering — if the
// grid derived filter keys from the columns it would emit graphqlField names the
// backend doesn't recognize and every filter would silently no-op.
//
// These lists are hardcoded (not derived) for clarity, and kept honest by
// pipelineRowFilters.test.ts, which fails if they drift from the field map's `keyed`
// shorthands.

// Field keys are the `keyed` shorthands from WASTE_MANIFEST_FIELD_MAP.
export const PIPELINE_ROW_FILTER_GROUPS: FilterGroup[] = [
  { name: "Manifest", fields: ["mtn", "shipped", "received", "reportable"] },
  { name: "Generator", fields: ["epaId", "state", "city"] },
  { name: "Vendor / Destination", fields: ["profile", "container", "uom"] },
  {
    name: "Classification",
    fields: ["class", "wasteCode", "tag", "dotHaz", "epa", "prov", "overridden"],
  },
];

// The editor + values must match how the field is actually stored, because the
// backend filters these with `=`:
//   - reportable is stored as the labels "Reportable" / "Not Reportable"
//   - dotHaz / epa / overridden are stored as "Y" / "N"
//   - isProvisional (prov) is a real boolean, stored as "true" / "false"
// so those are enums of the stored values / a boolean; dates get a date-range
// picker (the backend casts the stored MM/DD/YYYY to a date); the rest filter by
// substring (ILIKE / array OVERLAP).
const YES_NO_VALUES = ["Y", "N"];
export const PIPELINE_ROW_FILTER_KEYS: FilterKey[] = [
  { key: "mtn", header: "Manifest Tracking #", filterKeyType: "text" },
  { key: "shipped", header: "Shipped Date", filterKeyType: "date" },
  { key: "received", header: "Received Date", filterKeyType: "date" },
  {
    key: "reportable",
    header: "Reportable",
    filterKeyType: "enum",
    enumValues: ["Reportable", "Not Reportable"],
  },
  { key: "epaId", header: "Generator EPA ID", filterKeyType: "text" },
  { key: "state", header: "Generator State", filterKeyType: "text" },
  { key: "city", header: "Generator City", filterKeyType: "text" },
  { key: "profile", header: "Vendor Profile #", filterKeyType: "text" },
  { key: "container", header: "Container Type", filterKeyType: "text" },
  { key: "uom", header: "UoM", filterKeyType: "text" },
  { key: "class", header: "Classification", filterKeyType: "text" },
  { key: "wasteCode", header: "Waste Code", filterKeyType: "text" },
  { key: "tag", header: "Tags", filterKeyType: "text" },
  { key: "dotHaz", header: "DOT Hazardous", filterKeyType: "enum", enumValues: YES_NO_VALUES },
  { key: "epa", header: "EPA Waste", filterKeyType: "enum", enumValues: YES_NO_VALUES },
  { key: "prov", header: "Provisional", filterKeyType: "boolean" },
  {
    key: "overridden",
    header: "Override Manual Edits",
    filterKeyType: "enum",
    enumValues: YES_NO_VALUES,
  },
];

// The rejected views (groups grid + drill-in) run through a builder that also
// registers the rejection-metadata fields (REJECTED_DRILL_IN_EXTRA_FIELDS), so they
// can additionally filter on rejection category/reason. The matched grid's builder
// doesn't have these, so they're only offered here.
export const PIPELINE_REJECTED_FILTER_GROUPS: FilterGroup[] = [
  ...PIPELINE_ROW_FILTER_GROUPS,
  { name: "Rejection", fields: ["category", "reason"] },
];

export const PIPELINE_REJECTED_FILTER_KEYS: FilterKey[] = [
  ...PIPELINE_ROW_FILTER_KEYS,
  { key: "category", header: "Rejection Category", filterKeyType: "text" },
  { key: "reason", header: "Rejection Reason", filterKeyType: "text" },
];
